import type {
  DriverLicenceRow,
  DriverLicenceTypeStatus,
  VehicleType,
} from 'types/api.generated';

export const LICENCE_STATUS_LABEL: Record<DriverLicenceTypeStatus, string> = {
  not_submitted: 'Not submitted',
  pending: 'Under review',
  verified: 'Verified',
  rejected: 'Rejected',
  expired: 'Expired',
};

export const LICENCE_STATUS_BADGE: Record<DriverLicenceTypeStatus, string> = {
  not_submitted: 'bg-slate-100 text-slate-600',
  pending: 'bg-amber-50 text-amber-700',
  verified: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
  expired: 'bg-orange-50 text-orange-700',
};

export function formatVehicleType(type: VehicleType): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function formatLicenceExpiry(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Driver-facing status line for a licence row. */
export function getDriverLicenceStatusCopy(row: DriverLicenceRow): string {
  const type = formatVehicleType(row.vehicleType);
  switch (row.status) {
    case 'not_submitted':
      return `Submit licence for ${type}`;
    case 'pending':
      return 'Under review';
    case 'verified':
      return row.document
        ? `Verified until ${formatLicenceExpiry(row.document.expiresAt)}`
        : 'Verified';
    case 'rejected':
      return row.rejectionReason
        ? `Rejected: ${row.rejectionReason}`
        : 'Rejected — submit again';
    case 'expired':
      return 'Licence expired — renew';
    default:
      return LICENCE_STATUS_LABEL[row.status];
  }
}

/** Admin-facing status line (shorter). */
export function getAdminLicenceStatusCopy(row: DriverLicenceRow): string {
  if (row.status === 'verified' && row.document) {
    return `Verified until ${formatLicenceExpiry(row.document.expiresAt)}`;
  }
  return LICENCE_STATUS_LABEL[row.status];
}
